import {
  TextField,
  Avatar,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  InputAdornment,
  Typography,
  Box,
  Button,
  IconButton
} from '@mui/material'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { getObjectsWithFilter } from '../../api/apiClientCrud'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import 'dayjs/locale/fr'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import styled from 'styled-components'

import CloudUploadIcon from '@mui/icons-material/CloudUpload'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import { ColorPicker } from 'mui-color'
import { BsImage } from 'react-icons/bs'

import AvatarUser from '../misc/Avatar'
import Image from '../misc/Image'
import SelectField from './SelectField'
import MultiSelectField from './MultiSelectField'
import FreeSelectField from './FreeSelectField'
import ListSelectField from './ListSelectField'
import MyTableEditable from './table/MyTableEditable'
import { useFieldValidation } from '../hooks/useFieldValidation'
import TransferListButton from './TransfertList'
import MemoizedJodit from './MemoizedJodit'

dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(customParseFormat)

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1
})

const Field = forwardRef((props, ref) => {
  const {
    data,
    mode,
    field,
    onValueChange,
    onDateChange,
    handleChangeFile,
    handleChangeFiles,
    handleDelete,
    handleMoveImage,
    isTable,
    editRow,
    onRowDataChange,
    isSaved
  } = props

  const { hasError, errorMessage, validateField } = useFieldValidation(field, data)

  const handleTableDataChange = (payload) => {
    const currentRows = Array.isArray(data?.[field.name]) ? data[field.name] : []
    const nextRows = typeof payload === 'function' ? payload(currentRows) : payload

    if (Array.isArray(nextRows)) {
      onValueChange({ target: { name: field.name, value: nextRows } })
    } else if (nextRows && Array.isArray(nextRows[field.name])) {
      onValueChange({ target: { name: field.name, value: nextRows[field.name] } })
    }

    field.tableOptions?.setDataParent?.(payload)
  }

  useImperativeHandle(ref, () => ({ validateField, hasError, fieldTab: field.tab }), [
    validateField,
    hasError,
    field.tab
  ])

  const editorConfig = {
    readonly: false,
    height: 400,
    uploader: { insertImageAsBase64URI: true },
    toolbarAdaptive: false,
    toolbarSticky: false,
    toolbarButtonSize: 'middle',
    buttons: [
      'undo',
      'redo',
      '|',
      'bold',
      'italic',
      'underline',
      'strikethrough',
      'eraser',
      'ul',
      'ol',
      'outdent',
      'indent',
      '|',
      'font',
      'fontsize',
      'brush',
      'paragraph',
      '|',
      'image',
      'link',
      'table',
      '|',
      'source',
      '|',
      {
        name: 'apply',
        tooltip: 'Appliquer',
        icon: 'save',
        exec: (editor) => {
          const event = {
            target: {
              type: 'joditEditor',
              name: editor.id,
              value: editor.value
            }
          }
          onValueChange(event)
        }
      }
    ]
  }

  const handleNumericKeyDown = (e) => {
    const allowed = ['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight', 'Delete', 'Enter']
    const isNumber = /^[0-9]$/.test(e.key)
    const isDecimal = e.key === '.' || e.key === ','

    if (!allowed.includes(e.key) && !isNumber && !isDecimal) e.preventDefault()
  }

  const handleNumericChange = (e) => {
    const val = e.target.value.replace(',', '.')
    onValueChange({ target: { name: field.name, value: val } })
  }

  useEffect(() => {
    const fetchDefaultValue = async () => {
      try {
        const res = await getObjectsWithFilter(field.defaultValue.object, field.defaultValue.filter(data))
        const defaultVal = res.data[field.defaultValue.object]?.[0]?.[field.defaultValue.value] ?? null
        onValueChange({ target: { name: field.name, value: defaultVal } })
      } catch {
        onValueChange({ target: { name: field.name, value: null } })
      }
    }

    if (
      ['text', 'text-multiline', 'currency'].includes(field.type) &&
      field.defaultValue &&
      data[field.defaultValue.trigger] !== undefined &&
      field.defaultValue.type === 'api'
    ) {
      fetchDefaultValue()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data[field.defaultValue?.trigger]])

  const renderTextField = (fieldType = 'text', overrides = {}) => {
    const isPassword = fieldType === 'password'

    return (
      <TextField
        sx={{ my: 0.5, ...overrides.sx }}
        fullWidth={field.fullWidth ?? false}
        name={field.name}
        label={!isTable ? field.label : null}
        variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
        required={field.required || false}
        error={!!hasError}
        value={
          data[field.name] !== undefined && data[field.name] !== null && data[field.name] !== ''
            ? data[field.name]
            : field.defaultValue && typeof field.defaultValue === 'function'
              ? field.defaultValue()
              : ''
        }
        onChange={['float', 'currency', 'number'].includes(fieldType) ? handleNumericChange : onValueChange}
        slotProps={{
          input: {
            type: isPassword ? 'password' : ['float', 'currency'].includes(fieldType) ? 'number' : 'text',
            onKeyDown: ['float', 'currency', 'number'].includes(fieldType) ? handleNumericKeyDown : undefined,
            readOnly:
              mode === 'view' ||
              field.readOnly ||
              (field.enableEditing !== undefined && !field.enableEditing) ||
              (field.readOnlyWhen ? field.readOnlyWhen(editRow || data) : false),
            ...overrides.slotProps?.input
          },
          ...overrides.slotProps
        }}
      />
    )
  }

  const renderDateField = (type = 'date') => {
    const Picker = type === 'datetime' ? DateTimePicker : DatePicker
    const localFormat = 'YYYY-MM-DD HH:mm:ss'
    let value = null

    if (field.defaultValueFunction && field.defaultValueField && data[field.defaultValueField] !== undefined) {
      value = dayjs.utc(field.defaultValueFunction(data)).local()
    } else if (data[field.name]) {
      const dateString = data[field.name]
      const isUtcExplicit = /Z$|[+-]\d{2}:?\d{2}$/.test(dateString)
      value = isUtcExplicit ? dayjs.utc(dateString).local() : dayjs(dateString.replace('T', ' '), localFormat)
    }

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale='fr'>
        <Picker
          label={!isTable ? field.label : null}
          sx={{ my: 0.5 }}
          value={value}
          views={field.views}
          onChange={field.onDateChange || onDateChange(field.name, type)}
          readOnly={mode === 'view' || field.readOnly}
          slotProps={{
            textField: {
              fullWidth: field.fullWidth ?? false,
              error: !!hasError,
              variant: mode === 'view' || isTable ? 'standard' : 'outlined',
              required: field.required || false
            }
          }}
        />
      </LocalizationProvider>
    )
  }

  // Utility to add/replace timestamp query parameter in a safe way
  const addTimestampToUrl = (url) => {
    if (!url) return url
    try {
      // Use the URL API to reliably set the search param 't'
      const u = new URL(url, window.location.origin)
      u.searchParams.set('t', Date.now())
      return u.toString()
    } catch (e) {
      // Fallback if URL() fails (relative URL weirdness)
      return `${url}${url.includes('?') ? '&' : '?'}t=${Date.now()}`
    }
  }

  const buildField = (field) => {
    switch (field.type) {
      case 'password':
        return renderTextField('password')
      case 'select':
        return (
          <SelectField {...{ data, mode, field, onValueChange, errors: errorMessage, isError: hasError, isTable }} />
        )
      case 'freeselect':
        return (
          <FreeSelectField
            {...{
              field,
              isTable,
              onChangeF: field.onChangeF,
              optionsAPI: field.optionsAPI,
              dataId: field.dataId,
              fieldName: field.fieldName,
              editRow,
              onRowDataChange,
              isError: hasError,
              error: errorMessage
            }}
          />
        )
      case 'listSelect':
        return (
          <ListSelectField
            {...{
              field,
              isTable,
              onChangeF: field.onChangeF,
              optionsAPI: field.optionsAPI,
              dataId: field.dataId,
              editRow,
              onRowDataChange,
              isError: hasError,
              error: errorMessage
            }}
          />
        )
      case 'multiselect':
        return <MultiSelectField {...{ data, mode, field, onValueChange, error: errorMessage, isError: hasError }} />
      case 'color':
        return (
          <TextField
            variant='standard'
            label={isTable ? null : field.label}
            InputProps={{
              disableUnderline: true,
              startAdornment: (
                <ColorPicker
                  hideTextfield
                  value={data[field.name]}
                  onChange={(e) =>
                    mode !== 'view' && onValueChange({ target: { name: field.name, value: '#' + e.hex } })
                  }
                />
              )
            }}
          />
        )
      case 'component':
        return field.component(data)
      case 'date':
        return renderDateField('date')
      case 'datetime':
        return renderDateField('datetime')
      case 'label':
        return <Typography sx={{ mt: 0.25, mb: 0.25, color: 'rgba(0,0,0,0.38)' }}>{field.label}</Typography>
      case 'joditEditor':
        return (
          <MemoizedJodit
            key={field.name} // Garde une clé stable
            name={field.name}
            value={data[field.name] || ''}
            onBlur={onValueChange}
            config={{
              ...editorConfig,
              readonly: mode === 'view' || field.readOnly,
              autofocus: false, // Important pour éviter le scroll jump
              // On s'assure que le collage garde le style HTML
              askBeforePasteHTML: false,
              askBeforePasteFromWord: false,
              cleanHTML: {
                fillEmptyParagraph: false,
                removeEmptyElements: false,
                replaceOldTags: false
              },
              allowTags: {
                // Permet de ne pas supprimer les balises {{ }} si elles sont vues comme du texte
                '*': true
              },
              defaultActionOnPaste: 'insert_clear_html'
            }}
          />
        )
      case 'text':
        return renderTextField()
      case 'float':
        return renderTextField('float')
      case 'text-multiline':
        return (
          <TextField
            sx={{ my: 0.5 }}
            fullWidth={field.fullWidth ?? false}
            name={field.name}
            variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
            label={isTable ? null : field.label}
            multiline
            rows={4}
            required={field.required || false}
            error={!!hasError}
            value={data[field.name] ?? ''}
            slotProps={{
              input: {
                readOnly:
                  mode === 'view' || field.readOnly || (field.enableEditing !== undefined && !field.enableEditing)
              }
            }}
            onChange={onValueChange}
            inputRef={field.ref}
          />
        )
      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                name={field.name}
                checked={data[field.name] || false}
                onChange={onValueChange}
                disabled={mode === 'view' || (field.readOnlyWhen ? !field.readOnlyWhen() : false)}
              />
            }
            label={isTable ? null : field.label}
          />
        )
      case 'file':
        return (
          mode !== 'view' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', my: 0.5 }}>
              <Button
                component='label'
                variant={isTable ? 'text' : 'contained'}
                size={isTable ? 'small' : 'medium'}
                startIcon={<CloudUploadIcon fontSize={isTable ? 'small' : 'inherit'} />}
                sx={{
                  mb: data[field.name] && data[field.name].length > 0 ? 0.5 : 0, // Marge basse si fichier présent
                  py: isTable ? '4px' : undefined,
                  px: isTable ? '6px' : undefined,
                  minWidth: isTable ? 64 : undefined
                }}
              >
                {isTable ? 'Choisir' : 'Choisissez un fichier'}
                <VisuallyHiddenInput type='file' onChange={handleChangeFile(field)} />
              </Button>

              {data[field.name] && data[field.name].length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                  {data[field.name].map(
                    (file, index) =>
                      file.file && (
                        <Typography
                          key={index}
                          sx={{
                            fontSize: isTable ? 10 : 12, // Légèrement plus petit pour la table
                            border: '1px solid #ccc',
                            p: '1px 6px',
                            borderRadius: 1,
                            maxWidth: isTable ? 'fit-content' : 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            backgroundColor: '#f5f5f5' // Optionnel: pour mieux voir le badge sous le bouton
                          }}
                        >
                          {file.file.name}
                        </Typography>
                      )
                  )}
                </Box>
              )}
            </Box>
          )
        )
      case 'files': // new type for multiple images
        return (
          mode !== 'view' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', my: 0.5 }}>
              <Button
                component='label'
                variant={isTable ? 'text' : 'contained'}
                size={isTable ? 'small' : 'medium'}
                startIcon={<CloudUploadIcon fontSize={isTable ? 'small' : 'inherit'} />}
                sx={{
                  mb: 1,
                  py: isTable ? '4px' : undefined,
                  px: isTable ? '6px' : undefined,
                  minWidth: isTable ? 64 : undefined
                }}
              >
                {isTable ? 'Choisir' : 'Choisissez des fichiers'}
                <VisuallyHiddenInput type='file' multiple onChange={handleChangeFiles(field)} />
              </Button>

              {/* Display selected file names */}
              {data[field.name] && data[field.name].length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {data[field.name].map((file, index) => (
                    <Typography
                      key={index}
                      sx={{
                        fontSize: isTable ? 11 : 12,
                        border: '1px solid #ccc',
                        p: '2px 6px',
                        borderRadius: 1,
                        maxWidth: isTable ? 120 : 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {file.name}
                    </Typography>
                  ))}
                </Box>
              )}
            </Box>
          )
        )

      case 'avatar':
        return field.file && field.file(data) ? (
          <TextField
            variant='standard'
            label={isTable ? null : field.label}
            InputProps={{
              disableUnderline: true,
              startAdornment: <AvatarUser size='logo' img={field.file(data)} />
            }}
          />
        ) : (
          <Avatar sx={{ height: 120, width: 120 }}>
            <AccountCircleIcon />
          </Avatar>
        )
      case 'image': {
        const imgSource = field.file && field.file(data)
        // If we have a string URL, append/replace a single timestamp param safely.
        if (imgSource) {
          const imgProp = imgSource // Key only based on base path (without query) to avoid chaotic keys
          const imageKey = typeof imgSource === 'string' ? String(imgSource).split('?')[0] : 'new-upload'

          return (
            <Box
              key={imageKey}
              sx={{
                width: 120,
                height: 120,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden'
              }}
            >
              <Image img={imgProp} />
            </Box>
          )
        } else {
          // smaller placeholder inside table, keep original size elsewhere
          const placeholderWidth = isTable ? 120 : 160
          const placeholderHeight = isTable ? 100 : 120
          const iconSize = isTable ? 24 : 32 // px
          return (
            <Box
              sx={{
                height: placeholderHeight,
                width: placeholderWidth,
                border: '2px dashed #ccc',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#aaa'
              }}
            >
              <BsImage size={iconSize} />
            </Box>
          )
        }
      }
      case 'image-multi': {
        const imagesList = field.files ? field.files(data) : []
        const hasImages = Array.isArray(imagesList) && imagesList.length > 0
        const canReorderImages = mode === 'edit' && field.enableOrdering && typeof handleMoveImage === 'function'

        return hasImages ? (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {imagesList.map((imgData, index) => (
              <Box
                key={imgData?.id || imgData?.url || imgData?.file?.name || index}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5
                }}
              >
                <Box
                  sx={{
                    width: 120,
                    height: 120,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    overflow: 'hidden',
                    border: '1px solid #eee',
                    borderRadius: 1
                  }}
                >
                  <Image img={imgData} mode={mode} onDelete={() => handleDelete(imgData)} />
                </Box>

                {canReorderImages && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <IconButton
                      size='small'
                      onClick={() => handleMoveImage(imgData, -1)}
                      disabled={index === 0}
                      aria-label="Deplacer l'image vers la gauche"
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                    </IconButton>

                    <Typography variant='caption' sx={{ minWidth: 44, textAlign: 'center' }}>
                      {index + 1}
                    </Typography>

                    <IconButton
                      size='small'
                      onClick={() => handleMoveImage(imgData, 1)}
                      disabled={index === imagesList.length - 1}
                      aria-label="Deplacer l'image vers la droite"
                    >
                      <ArrowForwardIosIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  </Box>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          // adapt placeholder size if rendered in table context
          <Box
            sx={{
              height: 120,
              width: isTable ? 120 : 160,
              border: '2px dashed #ccc',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#aaa'
            }}
          >
            <BsImage size={isTable ? 24 : 32} />
          </Box>
        )
      }
      case 'currency':
        return (
          <TextField
            sx={{ my: 0.5 }}
            fullWidth={field.fullWidth ?? false}
            name={field.name}
            label={!isTable ? field.label : null}
            variant={mode === 'view' || isTable ? 'standard' : 'outlined'}
            required={field.required || false}
            error={!!hasError}
            type='number'
            inputProps={{ min: field.min, max: field.max, step: '0.01' }}
            InputProps={{
              startAdornment: <InputAdornment position='start'>{field.currency ?? '€'}</InputAdornment>
            }}
            slotProps={{
              input: {
                readOnly:
                  mode === 'view' || field.readOnly || (field.enableEditing !== undefined && !field.enableEditing)
              }
            }}
            value={data[field.name] ?? ''}
            onChange={onValueChange}
          />
        )
      case 'mytable':
        console.log('Field: rendering mytable', field.name, 'dataHasKey:', !!data?.[field.name])
        console.log('Field: field.tableOptions.setDataParent exists:', !!field.tableOptions?.setDataParent)
        return (
          <MyTableEditable
            data={data}
            tableOptions={field.tableOptions}
            alldata={data}
            mode={mode}
            field={field}
            setData={handleTableDataChange}
            error={errorMessage}
            isSaved={isSaved}
            columns={field.tableOptions.columns}
          />
        )
      case 'transferList':
        return (
          <TransferListButton
            data={data}
            field={field}
            allAttributes={field.options}
            mode={mode}
            onRowDataChange={onRowDataChange}
          />
        )
      default:
        return renderTextField()
    }
  }

  return (
    <>
      {buildField(field)}
      {hasError && <FormHelperText error>{errorMessage || ''}</FormHelperText>}
    </>
  )
})

export default Field
